/**
 * @file usePlanStorage.js
 * @description Hook específico para gestión de planes en el editor.
 * Proporciona funcionalidad CRUD para planes usando PostgreSQL como backend.
 * Fuente única de verdad para el estado de planes.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import planService from '../services/planService';
import { PlanModel } from '../services/models/plan';
import { useRoles } from './useRoles';

export const usePlanStorage = () => {
  const [rawPlans, setRawPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const { roles } = useRoles();

  // Live color lookup — single source of truth (Plan #70).
  const rolesIndex = useMemo(
    () => new Map((roles || []).map(r => [r.id, { color: r.color, name: r.name }])),
    [roles]
  );

  // plans is derived state: re-hydrates automatically when roles (colors) change.
  const plans = useMemo(
    () => rawPlans.map(d => PlanModel.fromBackend(d, rolesIndex).toFrontendFormat()),
    [rawPlans, rolesIndex]
  );

  // ✅ FIX: Ref to track if component is mounted for async operations
  const isMountedRef = useRef(true);

  // Reset ref on mount, set to false on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Función de recarga reutilizable
  const loadPlans = useCallback(async () => {
    if (authLoading) {
      console.log('[usePlanStorage] Esperando autenticación...');
      return;
    }
    if (!user?.id) {
      console.warn('[usePlanStorage] No hay usuario autenticado');
      if (isMountedRef.current) {
        setRawPlans([]);
        setLoading(false);
      }
      return;
    }
    if (isMountedRef.current) setLoading(true);
    try {
      const response = await planService.getPlans(user.id);
      // ✅ FIX: Check if still mounted before updating state
      if (!isMountedRef.current) return;

      if (response.success && response.data) {
        setRawPlans(response.data);
      } else {
        console.error('Error en respuesta del servidor:', response);
        setRawPlans([]);
      }
    } catch (error) {
      console.error("Error cargando planes desde PostgreSQL:", error);
      if (isMountedRef.current) setRawPlans([]);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [user, authLoading]);

  // useEffect inicial
  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Debug: Monitorear cambios en plans
  useEffect(() => {
    console.log('[usePlanStorage] Estado de plans actualizado:', plans.length, 'planes');
  }, [plans]);
  
  /**
   * Crea un nuevo plan
   * @param {Object} planData - Datos del plan a crear
   * @returns {Object} El plan creado o null si hay error
   */
  const createPlan = useCallback(async (planData) => {
    if (!planData) return null;

    console.log('[usePlanStorage] createPlan - INICIO', {
      planData,
      planDataNodes: planData.nodes,
      planDataEdges: planData.edges,
      planDataWorkflowData: planData.workflowData
    });

    try {
      console.log('[usePlanStorage] createPlan - Usando savePlanFromEditor');
      const newPlan = await planService.savePlanFromEditor(planData);
      console.log('[usePlanStorage] createPlan - Respuesta del backend', newPlan);

      setRawPlans(prev => [...prev, newPlan.data]);

      const frontendPlan = PlanModel.fromBackend(newPlan.data, rolesIndex).toFrontendFormat();
      console.log('[usePlanStorage] createPlan - Plan convertido', {
        frontendPlan,
        frontendPlanNodes: frontendPlan.nodes,
        frontendPlanEdges: frontendPlan.edges
      });
      return frontendPlan;
    } catch (error) {
      // Propagar, igual que en updatePlan: un fallo silencioso aquí se ve como
      // "Guardado" en la toolbar.
      console.error("Error creando plan:", error);
      throw error;
    }
  }, []);

  /**
   * Actualiza un plan existente
   * @param {Object} planData - Datos del plan a actualizar
   * @returns {Object} El plan actualizado o null si hay error
   */
  const updatePlan = useCallback(async (planData) => {
    if (!planData || !planData.id) return null;

    console.log('[usePlanStorage] updatePlan - INICIO', {
      planData,
      planDataId: planData.id,
      planDataNodes: planData.nodes,
      planDataEdges: planData.edges,
      planDataWorkflowData: planData.workflowData
    });

    try {
      console.log('[usePlanStorage] updatePlan - Enviando al backend:', {
        planId: planData.id,
        nodeCount: planData.nodes?.length || 0,
        edgeCount: planData.edges?.length || 0
      });
      
      const updatedPlan = await planService.savePlanFromEditor(planData);
      console.log('[usePlanStorage] updatePlan - Respuesta del backend:', updatedPlan);

      setRawPlans(prev => prev.map(p => p.id === updatedPlan.data.id ? updatedPlan.data : p));

      const frontendPlan = PlanModel.fromBackend(updatedPlan.data, rolesIndex).toFrontendFormat();
      console.log('[usePlanStorage] updatePlan - Plan convertido:', {
        frontendPlan,
        frontendPlanNodes: frontendPlan.nodes,
        frontendPlanEdges: frontendPlan.edges
      });
      return frontendPlan;
    } catch (error) {
      // Propagar: devolver null hacía que el editor marcase "Guardado" sobre un
      // guardado rechazado por el backend (p. ej. un plan con agentes borrados,
      // que `updatePlan` rechaza en bloque). El llamante decide qué enseñar.
      console.error("Error actualizando plan:", error);
      throw error;
    }
  }, []);

  /**
   * Guarda el JSON del plan tal cual, sin pasar por el canvas (#458).
   *
   * `savePlan` va por `savePlanFromEditor`, que regenera `planDataPure` desde los
   * nodos y renumera los ids de los pasos. Para quitar un paso con el agente
   * borrado eso no sirve: el resto de pasos debe quedar intacto, y el paso roto no
   * siempre está en el canvas con todos sus campos hidratados. Aquí se manda el
   * JSON ya operado por `removeStepFromPlan` y el backend hace un UPDATE parcial
   * de las dos columnas JSONB, recalculando `plan_rol_involved` de paso.
   *
   * @param {{id: string, planDataPure: object, planDataVisual: object|null}} params
   * @returns {Object|null} El plan guardado en formato frontend
   */
  const savePlanJson = useCallback(async ({ id, planDataPure, planDataVisual }) => {
    if (!id || !planDataPure) return null;

    try {
      const response = await planService.updatePlan(id, { planDataPure, planDataVisual });
      const saved = response?.data;
      if (!saved) return null;

      setRawPlans(prev => prev.map(p => (p.id === saved.id ? saved : p)));
      return PlanModel.fromBackend(saved, rolesIndex).toFrontendFormat();
    } catch (error) {
      console.error('Error guardando el JSON del plan:', error);
      throw error;
    }
  }, [rolesIndex]);

  /**
   * Elimina un plan
   * @param {string} planId - ID del plan a eliminar
   * @returns {boolean} True si se eliminó correctamente
   */
  const deletePlan = useCallback(async (planId) => {
    if (!planId) return false;

    try {
      await planService.deletePlan(planId);
      setRawPlans(prev => prev.filter(p => p.id !== planId));
      
      return true;
    } catch (error) {
      console.error("Error eliminando plan:", error);
      return false;
    }
  }, []);

  /**
   * Guarda un plan (crea o actualiza según si tiene ID)
   * @param {Object} planData - Datos del plan
   * @returns {Object} El plan guardado o null si hay error
   */
  const savePlan = useCallback(async (planData) => {
    if (!planData) return null;

    console.log('[usePlanStorage] savePlan - INICIO', { 
      planData, 
      hasId: !!planData.id,
      idValue: planData.id,
      idType: typeof planData.id
    });

    // Verificar si el plan tiene un ID válido (no null, undefined, o string vacío)
    const hasValidId = planData.id && 
                       planData.id !== null && 
                       planData.id !== undefined && 
                       planData.id !== '';

    if (hasValidId) {
      console.log('[usePlanStorage] savePlan - Actualizando plan existente con ID:', planData.id);
      return await updatePlan(planData);
    } else {
      console.log('[usePlanStorage] savePlan - Creando nuevo plan (sin ID válido):', planData.id);
      return await createPlan(planData);
    }
  }, [createPlan, updatePlan]);

  return {
    plans,
    loading,
    createPlan,
    updatePlan,
    deletePlan,
    savePlan,
    savePlanJson,
    reloadPlans: loadPlans
  };
};
